import { useEffect, useRef } from 'react'
import { CheckCircle2, Clock, X } from 'lucide-react'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import VimeoEmbed from '@/components/features/video/VimeoEmbed'
import { useFirstAccessSequence } from './useFirstAccessSequence'

/**
 * MH14-04 — pop-up com as aulas "Obrigatória no primeiro acesso", em sequencia, na
 * primeira entrada do aluno (montado no DashboardLayout, depois das guardas de aceite,
 * plano, onboarding e contrato). O video abre direto, sem tela de aviso antes.
 *
 * "Já assisti" marca a aula como assistida; "Ver depois" so avanca, sem marcar. Fechar
 * ou terminar esconde ate o fim da sessao (MH14-04b): a aula sem "Já assisti" volta na
 * proxima entrada. Admin e instrutor nem consultam o servidor (o hook so roda para
 * aluno).
 */
export function FirstAccessVideosPopup() {
  const { current, position, total, saving, error, watched, later, close } = useFirstAccessSequence()
  const primaryRef = useRef<HTMLButtonElement>(null)
  const lessonId = current?.lesson_id

  // Foco no botao principal a cada video; Esc fecha (o mesmo que o X).
  useEffect(() => {
    if (lessonId === undefined) return
    primaryRef.current?.focus()
  }, [lessonId])

  useEffect(() => {
    if (lessonId === undefined) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [lessonId, close])

  if (!current) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-access-video-title"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm sm:items-center"
    >
      {/* Largura limitada pela altura da tela: em celular deitado o video 16:9 nao
          empurra os botoes para fora. */}
      <Card
        variant="elevated"
        padding="none"
        className="my-auto w-full max-w-[min(48rem,calc((100dvh-14rem)*16/9))] min-w-[min(100%,20rem)]"
      >
        <CardHeader className="flex items-center justify-between gap-3 border-b border-neutral-200 p-4 dark:border-neutral-700">
          <div className="min-w-0">
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
              Vídeo {position} de {total}
            </p>
            <CardTitle id="first-access-video-title" className="truncate">
              {current.title}
            </CardTitle>
          </div>
          <button
            type="button"
            onClick={close}
            className="text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </CardHeader>
        <CardContent className="space-y-4 p-4">
          {/* key: remonta o iframe a cada video, senao o anterior continua tocando. */}
          <VimeoEmbed key={current.lesson_id} embedUrl={current.embed_url} title={current.title} />
          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              As aulas ficam disponíveis em Vídeo-aulas.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="button" variant="outline" onClick={later} disabled={saving}>
                <Clock className="h-4 w-4" />
                Ver depois
              </Button>
              <Button ref={primaryRef} type="button" onClick={watched} disabled={saving}>
                <CheckCircle2 className="h-4 w-4" />
                Já assisti
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
